import { ReactPortal, useCallback, useMemo } from "react";
import styled from "styled-components";
import { MODAL_PORTAL_ID } from "@sablier/v2-constants";
import { useModal } from "@sablier/v2-contexts";
import { useDebounce, useScrollDisable } from "@sablier/v2-hooks";
import { _ } from "@sablier/v2-mixins";
import { createPortal } from "react-dom";
import { Backdrop, Conditional } from "~/components/atoms";
import { Header } from "~/components/molecules";
import type { ModalListProps } from "./types";
import type { MouseEvent, PropsWithChildren } from "react";

const WrapperPartial = styled.div<{ elevation?: number }>`
  --background-modal: ${(props) => props.theme.colors.modalBackground};
  ${(props) => props.theme.styles.row}
  & {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    z-index: ${(props) =>
      (props.elevation || 0) + props.theme.sizes.zIndexList};
    justify-content: center;
    width: 100vw;
    height: 100vh;
    overflow-x: hidden;
    overflow-y: hidden;
  }
`;

const ModalBackdrop = styled(Backdrop)`
  z-index: ${(props) => props.theme.sizes.zIndexUnder};
`;

const Scrollable = styled.div`
  z-index: ${(props) => props.theme.sizes.zIndexOver};
  width: 100%;
  height: 100%;
  overflow-y: auto;
`;

const Container = styled.div`
  ${(props) => props.theme.styles.contained}
  ${(props) => props.theme.styles.column}
  & {
    justify-content: flex-start;
    height: 100%;
    pointer-events: none;
    & > * {
      pointer-events: auto;
    }
  }
`;

const Column = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    padding-top: calc(${(props) => props.theme.sizes.edge} * 3);
    padding-bottom: calc(${(props) => props.theme.sizes.edge} * 2);
  }
`;

const List = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    row-gap: calc(${(props) => props.theme.sizes.edge});
    padding-bottom: calc(${(props) => props.theme.sizes.edge} * 4);
  }
`;

const Wrapper = styled(WrapperPartial)`
  opacity: 1;
  transition: opacity 150ms;

  ${List} {
    transform: translateY(0px);
    transition: transform 250ms;
  }

  &[data-visible="false"] {
    opacity: 0;
    pointer-events: none;
    transition: opacity 150ms;

    ${List} {
      transform: translateY(10px);
      transition: transform 250ms;
    }
  }

  &[data-disabled="true"] {
    pointer-events: none;
    &:after {
      position: fixed;
      top: 0;
      left: 0;
      content: "";
      z-index: 8000;
      width: 100vw;
      height: 100vh;
      background: rgba(0, 0, 0, 0.4);
    }
  }

  ${(props) => props.theme.medias.maxLG} {
    ${List} {
      max-width: 100%;
    }
  }

  ${(props) => props.theme.medias.maxMD} {
    ${Column} {
      padding-top: calc(${(props) => props.theme.sizes.edge} * 1 / 2);
      padding-bottom: calc(${(props) => props.theme.sizes.edge} * 1);
    }
    ${List} {
      row-gap: calc(${(props) => props.theme.sizes.edge} * 1 / 2);
    }
    header {
      padding-top: calc(${(props) => props.theme.sizes.edge} * 1 / 2);
      padding-bottom: calc(${(props) => props.theme.sizes.edge} * 1);
    }
  }

  ${(props) => props.theme.medias.maxXS} {
    transition-duration: 0ms;
    ${List} {
      transition-duration: 0ms;
    }

    &[data-visible="false"] {
      transition-duration: 0ms;

      ${List} {
        transition-duration: 0ms;
      }
    }
  }
`;

function ModalList({
  children,
  className,
  elevation,
  id,
  isBackdropDisabled = false,
  isCloseDisabled = false,
  isDisabled = false,
  title,
}: PropsWithChildren<ModalListProps>): ReactPortal | false {
  const { doClose, isOpen: isOpenLive } = useModal(id);
  const isOpenDelayed = useDebounce(isOpenLive, 100);

  useScrollDisable(isOpenLive);

  const onBackdrop = useCallback(() => {
    if (!isBackdropDisabled) {
      doClose();
    }
  }, [isBackdropDisabled, doClose]);

  const doStopPropagation = useCallback((event: MouseEvent) => {
    event.stopPropagation();
  }, []);

  const portal = useMemo(
    () => (!_.isWindow() ? false : document.getElementById(MODAL_PORTAL_ID)),
    [],
  );

  if (!portal) {
    return false;
  }

  return createPortal(
    <Wrapper
      className={className}
      data-visible={isOpenDelayed}
      elevation={elevation}
      id={id}
      data-disabled={isDisabled}
    >
      <Conditional on={isOpenLive}>
        <Scrollable onClick={onBackdrop}>
          <Container onClick={doStopPropagation}>
            <Column>
              {title && !isCloseDisabled && (
                <Header back={doClose} title={title} />
              )}
              <List data-component={"modal-list"}>{children}</List>
            </Column>
          </Container>
        </Scrollable>
      </Conditional>
      <ModalBackdrop isBacklit isOpen={isOpenLive} onClick={onBackdrop} />
    </Wrapper>,
    portal,
  );
}

export default ModalList;
